import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Filter } from '@travel/icons/ui';
import type { SortKeysType } from '@travel/traveler-core/constants/provider';
import { useDeviceType, useDialogHandler } from '@travel/traveler-core/hooks';
import { seoKeys } from '@travel/traveler-core/utils/seo';
import { IconWithBadge } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import { getRequestBody } from 'pages/ProviderListPage/resolver';

import { fetchProviderFilters } from 'store/providerList/actions';
import { getFilters, getSelectedObjectivesFeatureIdMapping } from 'store/providerList/selectors';
import { getCouponDetails } from 'store/salesPromotion/selectors';

import { DEFAULT_SORT_KEY, EMPTY_ARRAY } from 'constants/defaultValue';
import { FILTER_GROUP_IDS } from 'constants/provider';
import useProviderFeatureFilter from 'hooks/useProviderFeatureFilter';
import type { SearchFormQuery } from 'ProviderList-Types';

import useClearFilterButton from '../../useClearFilterButton';
import FilterAndSort from '../FilterAndSort';

import styles from './filterContainer.module.scss';

export type PriceRangeType = number | null | undefined;

type ToggleOptionsType = {
  features?: string;
  maxPrice?: string;
  minPrice?: string;
  freeCancellation?: string;
  sortKey?: SortKeysType;
  'starRatings[]'?: string[];
  'reviewScores[]'?: string[];
};

type Props = {
  /** on what parent page this component lives */
  onPage: 'providerListUndated' | 'providerListDated';
  className?: string;
  filterButtonClassName?: string;
  /** String of recent path id */
  pathId: string;
  /** Object of recent search query */
  query: SearchFormQuery;
  /** Callback to be called when search button has been clicked */
  onSearch: () => void;
  /** Callback to be called when remove selected coupon */
  onRemoveCoupon?: () => void;
  setSortKey?: React.Dispatch<React.SetStateAction<SortKeysType>>;
};

export const toggleFilterOption = (currentOptions: Array<string>, targetOption: string) => {
  if (currentOptions.includes(targetOption)) {
    return currentOptions.filter(opt => opt !== targetOption);
  }

  return [...currentOptions, targetOption];
};

function FilterContainer(props: Props) {
  const dispatch = useDispatch();
  const { query, onPage, onSearch } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const deviceType = useDeviceType();
  const isMobile = deviceType === 'sp';

  const filters = useSelector(getFilters);
  const objectiveFeatureMappingIds = useSelector(getSelectedObjectivesFeatureIdMapping);

  const couponId = props.query?.salesPromotionId;
  const couponDetail = useSelector(getCouponDetails(couponId));

  const {
    isOpen: isDialogOpen,
    onToggle: onToggleDialogHook,
    onClose: onCloseFilterDialog,
  } = useDialogHandler(false);

  const searchFreeCancellation = query.freeCancellation === 'true';
  const searchMaxPrice = query.maxPrice ? Number(query.maxPrice) : null;
  const searchMinPrice = query.minPrice ? Number(query.minPrice) : null;
  const searchStarRating = query['starRatings[]'] || EMPTY_ARRAY;
  const searchReviewRating = query['reviewScores[]'] || EMPTY_ARRAY;

  const recentPriceFilter = filters.find(filter => filter.groupId === FILTER_GROUP_IDS.PRICE);
  const [isClearFilterButtonDisabled, setIsClearFilterButtonDisabled] = useState<boolean>(false);
  const [maxPrice, setMaxPrice] = useState<PriceRangeType>(searchMaxPrice);
  const [minPrice, setMinPrice] = useState<PriceRangeType>(searchMinPrice);
  const [starRating, setStarRating] = useState<Array<string>>(searchStarRating);
  const [reviewRating, setReviewRating] = useState<Array<string>>(searchReviewRating);
  const [freeCancellation, setFreeCancellation] = useState(searchFreeCancellation);
  const [sortKey, setSortKey] = useState(props.query?.sortKey || DEFAULT_SORT_KEY);
  const { features, setFeatures, searchFeatures } = useProviderFeatureFilter(
    objectiveFeatureMappingIds,
    query.features,
  );

  const filterNumber =
    searchFeatures.length +
    ~~!!searchMaxPrice +
    ~~isNotEmptyArray(searchStarRating) +
    ~~isNotEmptyArray(searchReviewRating) +
    ~~freeCancellation;

  useClearFilterButton({
    features,
    freeCancellation,
    isClearFilterButtonDisabled,
    maxPrice,
    minPrice,
    recentPriceFilter,
    reviewRating,
    setIsClearFilterButtonDisabled,
    sortKey,
    starRating,
  });

  const onClickAwayDialog = useCallback(() => {
    onCloseFilterDialog();
    // reset filter state to the recent searched one
    setMinPrice(searchMinPrice);
    setMaxPrice(searchMaxPrice);
    setStarRating(searchStarRating);
    setReviewRating(searchReviewRating);
    setFeatures(searchFeatures);
    setFreeCancellation(searchFreeCancellation);

    onFilterChange({
      minPrice: searchMinPrice?.toString(),
      maxPrice: searchMaxPrice?.toString(),
      features: searchFeatures.toString(),
      'starRatings[]': searchStarRating,
      'reviewScores[]': searchReviewRating,
      freeCancellation: String(searchFreeCancellation),
    });
  }, [
    onCloseFilterDialog,
    searchFeatures,
    searchMaxPrice,
    searchMinPrice,
    searchReviewRating,
    searchStarRating,
    setFeatures,
    searchFreeCancellation,
  ]);

  const toggleFeatureFilterOptions = useCallback(
    (newFeatures: string[]) => {
      let nextFeatures = [...features];

      if (isNotEmptyArray(newFeatures)) {
        const filteredFeatures = features.filter(feature => !newFeatures?.includes(feature));
        const newlyAddedFeatures = newFeatures.filter(feature => !features?.includes(feature));

        nextFeatures = [...filteredFeatures, ...newlyAddedFeatures];
      }

      setFeatures(nextFeatures);
      return nextFeatures;
    },
    [features, setFeatures],
  );

  const onFilterChange = (toggleOptions: ToggleOptionsType) => {
    const newQuery = {
      ...query,
      // faceting endpoint won't use min-max price anymore due to requirement change
      minPrice: '',
      maxPrice: '',
      features: features?.toString(),
      sortKey: sortKey,
      'starRatings[]': starRating,
      'reviewScores[]': reviewRating,
      freeCancellation: freeCancellation.toString(),
      ...toggleOptions,
    };

    dispatch(
      fetchProviderFilters(
        getRequestBody(
          {
            params: { pageIdentifier: props.pathId },
            query: newQuery,
          },
          couponDetail,
        ),
      ),
    );
  };

  const onSearchClick = useCallback(() => {
    onCloseFilterDialog();
    onSearch?.();
  }, [onCloseFilterDialog, onSearch]);

  const onCheckboxChanged = (filterGroupId: string, toggleOption: string) => {
    switch (filterGroupId) {
      case FILTER_GROUP_IDS.SORT: {
        setSortKey(toggleOption as SortKeysType);
        onFilterChange({ sortKey: toggleOption as SortKeysType });
        break;
      }
      case FILTER_GROUP_IDS.RATING: {
        const nextRatings: Array<string> = toggleFilterOption(starRating, toggleOption);
        setStarRating(nextRatings);
        onFilterChange({ 'starRatings[]': nextRatings });
        break;
      }
      case FILTER_GROUP_IDS.REVIEW_SCORE: {
        const nextReviewScores: Array<string> = toggleFilterOption(reviewRating, toggleOption);
        setReviewRating(nextReviewScores);
        onFilterChange({ 'reviewScores[]': nextReviewScores });
        break;
      }
      case FILTER_GROUP_IDS.CANCELLATION_POLICY: {
        setFreeCancellation(!freeCancellation);
        onFilterChange?.({ freeCancellation: String(!freeCancellation) });
        break;
      }
      default: {
        const nextFeatures = toggleFeatureFilterOptions([toggleOption]);
        onFilterChange?.({ features: nextFeatures.toString() });
        break;
      }
    }
  };

  const onPriceRangeChanged = (min?: number, max?: number) => {
    if (Number(min) < 0) {
      return;
    }

    setMinPrice(min);
    setMaxPrice(max);
  };

  const onClearClick = () => {
    setMinPrice(null);
    setMaxPrice(null);
    setFeatures(EMPTY_ARRAY);
    setStarRating(EMPTY_ARRAY);
    setReviewRating(EMPTY_ARRAY);
    setSortKey(DEFAULT_SORT_KEY);
    setFreeCancellation(false);

    onFilterChange({
      minPrice: '',
      maxPrice: '',
      features: '',
      sortKey: DEFAULT_SORT_KEY,
      'starRatings[]': EMPTY_ARRAY,
      'reviewScores[]': EMPTY_ARRAY,
      freeCancellation: 'false',
    });

    setIsClearFilterButtonDisabled(true);
  };

  const appliedMin = minPrice ?? recentPriceFilter?.min;
  const appliedMax = maxPrice ?? recentPriceFilter?.max;

  // Dialog
  const onToggleFilterDialog = () => {
    if (isDialogOpen) {
      // if it's going to be closed without action, then reset to prev state
      onClickAwayDialog();
    } else {
      onToggleDialogHook();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={cx(props.className, styles.wrapper)}
      data-testid="filterContainer-wrapper"
    >
      {isNotEmptyArray(filters) && isMobile && (
        <button
          type="button"
          className={cx(styles.filterButton, props.filterButtonClassName)}
          onClick={onToggleFilterDialog}
          data-testid="filterContainer-toggleFilter-flatButton"
          aria-label="Filter"
        >
          <IconWithBadge
            badgeClassName={styles.filterBadge}
            icon={<Filter size={24} />}
            number={filterNumber}
          />
        </button>
      )}

      <FilterAndSort
        isClearFilterButtonDisabled={isClearFilterButtonDisabled}
        // dialog
        onPage={onPage}
        isOpen={isDialogOpen}
        onClose={onToggleFilterDialog}
        onSearch={onSearchClick}
        onClearClick={onClearClick}
        // search
        filters={filters}
        minPrice={appliedMin}
        maxPrice={appliedMax}
        starRating={starRating}
        reviewRating={reviewRating}
        features={features}
        sortKey={sortKey}
        onCheckboxChanged={onCheckboxChanged}
        onPriceRangeChanged={onPriceRangeChanged}
        freeCancellation={freeCancellation}
      />

      {minPrice && <input type="hidden" name="minPrice" value={String(minPrice)} />}
      {maxPrice && <input type="hidden" name="maxPrice" value={String(maxPrice)} />}
      {isNotEmptyArray(starRating) &&
        starRating.map(rating => (
          <input key={rating} type="hidden" name="starRatings[]" value={rating} />
        ))}
      {isNotEmptyArray(reviewRating) &&
        reviewRating.map(score => (
          <input key={score} type="hidden" name="reviewScores[]" value={score} />
        ))}
      {freeCancellation && (
        <input type="hidden" name="freeCancellation" value={freeCancellation.toString()} />
      )}
      {isNotEmptyArray(features) && (
        <input type="hidden" name="features" value={features?.join(',')} />
      )}
      {/* SEO Params */}
      {sortKey && <input type="hidden" name={seoKeys.sortKey} value={sortKey} />}
    </div>
  );
}

export default FilterContainer;
